import { MessageCircle, Star } from "lucide-react";
import { MaskedLine, Parallax, Reveal } from "@/components/Reveal";
import { useTexts } from "@/lib/texts";
import { waLink } from "@/lib/constants";

const TIMELINE = [
  {
    year: "2009",
    title: "Il primo banco",
    text: "Tutto inizia a Monza, in Viale Libertà 29, con un banco e un'idea semplice: valutare l'oro delle persone con onestà, davanti ai loro occhi. È così che nasce la nostra reputazione.",
    img: "/assets/storia-monza-2009.png",
    alt: "Il primo negozio Oro Jewels a Monza, Viale Libertà 29",
  },
  {
    year: "2013",
    title: "L'arrivo a Milano",
    text: "La fiducia cresce e Oro Jewels arriva anche a Milano: apre la sede di Via Nicola Antonio Porpora 156, dove al ritiro dell'oro si affianca la vendita di gioielli e orologi di prestigio, selezionati uno a uno.",
    img: "/assets/storia-porpora-2013.png",
    alt: "La boutique Oro Jewels di Via Porpora 156, Milano",
  },
  {
    year: "2015",
    title: "Il terzo negozio",
    text: "Nel 2015 apre il terzo negozio, sempre a Milano, in Via Gioacchino Murat 60: un nuovo punto di riferimento per chi cerca professionalità, riservatezza e il giusto valore per i propri preziosi.",
    img: "/assets/storia-murat-2015.png",
    alt: "Il negozio Oro Jewels di Via Murat 60, Milano",
  },
  {
    year: "Oggi",
    title: "Tre negozi, una promessa",
    text: "Con oltre 700 recensioni dei nostri clienti, oggi ci trovi a Milano e a Monza: tre vetrine, un'unica promessa, da noi i tuoi gioielli valgono di più, in vendita come in acquisto. Ti aspettano operatori professionisti, sempre a tua disposizione, per una valutazione onesta e un consiglio sincero.",
    img: "/assets/storia-oggi.png",
    alt: "Il team Oro Jewels al banco delle valutazioni",
  },
];

const STATS = [
  { value: "17", label: "anni di attività" },
  { value: "3", label: "negozi" },
  { value: "4.7", label: "su Google", star: true },
  { value: "233", label: "recensioni" },
];

export default function Storia() {
  const texts = useTexts();
  return (
    <main className="pb-28 pt-32 md:pt-40" data-testid="storia-page">
      <div className="mx-auto max-w-7xl px-5 md:px-8">
        <Reveal>
          <p className="text-[10px] uppercase tracking-[0.4em] text-gold">Chi siamo</p>
        </Reveal>
        <h1 className="mt-4 font-serif text-5xl leading-[1.02] md:text-7xl">
          <MaskedLine delay={0.1}>{texts.storia_title1}</MaskedLine>
          <MaskedLine delay={0.25}>
            {texts.storia_title2a} <em className="text-gold-gradient">{texts.storia_title2_gold}</em>{texts.storia_title2b}
          </MaskedLine>
        </h1>
        <Reveal delay={0.4}>
          <p className="mt-10 max-w-2xl border-l-2 border-gold/50 pl-6 font-serif text-lg italic leading-relaxed text-[#E5E5E5] md:text-xl" data-testid="storia-sub">
            {texts.storia_sub}
          </p>
        </Reveal>
        <Reveal delay={0.55}>
          <p className="mt-6 max-w-xl text-base leading-relaxed text-[#A3A3A3]" data-testid="storia-sub2">
            {texts.storia_sub2}
          </p>
        </Reveal>
      </div>

      <div className="mx-auto mt-24 max-w-7xl px-5 md:px-8">
        <div className="grid gap-px border border-white/10 bg-white/10 sm:grid-cols-2 lg:grid-cols-4" data-testid="storia-stats">
          {STATS.map((s, i) => (
            <Reveal key={s.label} delay={i * 0.1} className="bg-[#050505]">
              <div className="p-8 text-center md:p-10">
                <p className="flex items-center justify-center gap-2 font-serif text-5xl text-gold md:text-6xl">
                  {s.value}
                  {s.star && <Star size={24} className="fill-gold text-gold" />}
                </p>
                <p className="mt-3 text-[10px] uppercase tracking-[0.3em] text-[#A3A3A3]">{s.label}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>

      <div className="mx-auto mt-28 max-w-7xl px-5 md:mt-40 md:px-8">
        <div className="space-y-24 md:space-y-32">
          {TIMELINE.map((t, i) => (
            <div key={t.year} className="grid items-center gap-10 lg:grid-cols-12" data-testid={`storia-chapter-${i}`}>
              <div className={`lg:col-span-5 ${i % 2 === 1 ? "lg:order-2" : ""}`}>
                <Reveal>
                  <p className="outline-number font-serif text-6xl md:text-8xl">{t.year}</p>
                </Reveal>
                <MaskedLine className="mt-4 font-serif text-3xl md:text-4xl" delay={0.15}>{t.title}</MaskedLine>
                <Reveal delay={0.25}>
                  <p className="mt-5 max-w-md text-base leading-relaxed text-[#A3A3A3] md:text-lg">{t.text}</p>
                </Reveal>
              </div>
              <div className={`lg:col-span-7 ${i % 2 === 1 ? "lg:order-1" : ""}`}>
                <Parallax amount={35}>
                  <div className="relative">
                    <div className="gold-spotlight inset-[-10%] h-[120%] w-[120%]" />
                    <div className="relative overflow-hidden bg-[#0a0a0a]">
                      <img
                        src={t.img}
                        alt={t.alt}
                        loading="lazy"
                        data-testid={`storia-image-${i}`}
                        className="aspect-[16/10] w-full object-contain"
                      />
                    </div>
                  </div>
                </Parallax>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="relative mx-auto mt-28 max-w-4xl px-5 text-center md:mt-40">
        <div className="gold-spotlight left-1/2 top-1/2 h-[50vh] w-[90vw] -translate-x-1/2 -translate-y-1/2" />
        <MaskedLine className="relative font-serif text-3xl italic leading-snug md:text-5xl">
          "La fiducia si pesa in grammi,
        </MaskedLine>
        <MaskedLine className="relative font-serif text-3xl italic leading-snug md:text-5xl" delay={0.15}>
          ma si misura in anni."
        </MaskedLine>
        <Reveal delay={0.35}>
          <a
            href={waLink("Buongiorno, vorrei conoscere meglio Oro Jewels.")}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="storia-whatsapp-cta"
            className="relative mt-12 inline-flex items-center gap-3 bg-gold px-7 py-4 text-[11px] font-semibold uppercase tracking-[0.25em] text-black transition-all duration-300 hover:-translate-y-0.5 hover:bg-gold-hover"
          >
            <MessageCircle size={15} />
            Parliamone su WhatsApp
          </a>
        </Reveal>
      </div>
    </main>
  );
}
